/**
 * Minimal QR-code SVG renderer — byte mode, EC level M, versions 1-10.
 * Pure JS, no dependencies. Outputs an inline <svg>.
 */

// ── Galois Field GF(256) arithmetic ──────────────────────────────────────────
const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);
(function () {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    GF_EXP[i] = x; GF_LOG[x] = i;
    x <<= 1; if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) GF_EXP[i] = GF_EXP[i - 255];
})();
const gfMul = (a, b) => a && b ? GF_EXP[GF_LOG[a] + GF_LOG[b]] : 0;
function rsGeneratorPoly(degree) {
  const g = new Uint8Array(degree + 1); g[0] = 1;
  for (let i = 0; i < degree; i++) {
    for (let j = i; j >= 0; j--) {
      g[j + 1] ^= gfMul(g[j], GF_EXP[i]);
      if (j) g[j] = gfMul(g[j], GF_EXP[i]) ^ g[j - 1];
      else g[j] = gfMul(g[j], GF_EXP[i]);
    }
  }
  return g;
}
function rsEncode(data, nEC) {
  const gen = rsGeneratorPoly(nEC);
  const msg = new Uint8Array(data.length + nEC);
  msg.set(data);
  for (let i = 0; i < data.length; i++) {
    const c = msg[i];
    if (c) for (let j = 0; j < gen.length - 1; j++) msg[i + j + 1] ^= gfMul(c, gen[j]);
  }
  return msg.slice(data.length);
}

// ── QR version / capacity tables (byte mode, EC M) ───────────────────────────
// [dataCodewords, ecCodewords, modules]
const VERSION_TABLE = [
  null,
  [16, 10, 21], [28, 16, 25], [44, 26, 29], [64, 18, 33], [86, 24, 37],
  [108, 16, 41], [124, 18, 45], [154, 22, 49], [182, 22, 53], [216, 26, 57],
];
// For EC level M, data capacity (bytes) per version
const DATA_CAPACITY = [0, 14, 26, 42, 62, 84, 106, 122, 152, 180, 213];

function pickVersion(byteLen) {
  for (let v = 1; v <= 10; v++) if (DATA_CAPACITY[v] >= byteLen) return v;
  throw new Error('Data too long for QR version 1-10');
}

// ── Format info bits (EC level M, masks 0-7) ─────────────────────────────────
const FORMAT_BITS = [0x5412, 0x5125, 0x5e7c, 0x5b4b, 0x45f9, 0x40ce, 0x4f97, 0x4aa0];

// ── Module grid builder ───────────────────────────────────────────────────────
function buildMatrix(version) {
  const size = 4 * version + 17;
  const mod = Array.from({ length: size }, () => new Int8Array(size)); // 0=empty, 1=dark, -1=light
  const isFn = Array.from({ length: size }, () => new Uint8Array(size));

  function setMod(r, c, dark, fn) {
    mod[r][c] = dark ? 1 : -1;
    if (fn) isFn[r][c] = 1;
  }

  // Finder patterns
  function finder(row, col) {
    for (let dr = -1; dr <= 7; dr++) for (let dc = -1; dc <= 7; dc++) {
      const r = row + dr, c = col + dc;
      if (r < 0 || r >= size || c < 0 || c >= size) continue;
      const dark = (dr >= 0 && dr <= 6 && (dc === 0 || dc === 6)) ||
        (dc >= 0 && dc <= 6 && (dr === 0 || dr === 6)) ||
        (dr >= 2 && dr <= 4 && dc >= 2 && dc <= 4);
      setMod(r, c, dark, true);
    }
  }
  finder(0, 0); finder(0, size - 7); finder(size - 7, 0);

  // Timing strips
  for (let i = 8; i < size - 8; i++) {
    setMod(6, i, i % 2 === 0, true);
    setMod(i, 6, i % 2 === 0, true);
  }

  // Dark module
  setMod(size - 8, 8, true, true);

  // Alignment patterns (version >= 2)
  const ALIGN = [[], [], [6, 18], [6, 22], [6, 26], [6, 30], [6, 34],
    [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50]];
  const ap = ALIGN[version] || [];
  for (let ai = 0; ai < ap.length; ai++) for (let bi = 0; bi < ap.length; bi++) {
    const r = ap[ai], c = ap[bi];
    if (isFn[r][c]) continue;
    for (let dr = -2; dr <= 2; dr++) for (let dc = -2; dc <= 2; dc++) {
      const dark = Math.max(Math.abs(dr), Math.abs(dc)) !== 1;
      setMod(r + dr, c + dc, dark, true);
    }
  }

  return { mod, isFn, size };
}

function placeFormatInfo(mod, isFn, size, maskId) {
  const bits = FORMAT_BITS[maskId];
  const order = [0, 1, 2, 3, 4, 5, 7, 8, 14, 13, 12, 11, 10, 9, 8, 7];
  for (let i = 0; i < 15; i++) {
    const dark = (bits >> i & 1) === 1;
    // Around top-left finder
    const row = order[i], col = order[14 - i];
    if (row <= 8 && col <= 8) { mod[row][8] = dark ? 1 : -1; mod[8][col] = dark ? 1 : -1; }
    // Around the other two finders
    if (i < 8) { mod[size - 1 - i][8] = dark ? 1 : -1; }
    else { mod[8][size - 7 + (i - 8)] = dark ? 1 : -1; }
  }
}

function encodeData(text, version) {
  const bytes = new TextEncoder().encode(text);
  const [dataCW] = VERSION_TABLE[version];
  const bits = [];
  // Mode indicator: 0100 (byte)
  [0, 1, 0, 0].forEach(b => bits.push(b));
  // Char count (8 bits for v1-9)
  for (let i = 7; i >= 0; i--) bits.push((bytes.length >> i) & 1);
  // Data bytes
  for (const b of bytes) for (let i = 7; i >= 0; i--) bits.push((b >> i) & 1);
  // Terminator
  for (let t = 0; t < 4 && bits.length < dataCW * 8; t++) bits.push(0);
  while (bits.length % 8 !== 0) bits.push(0);
  // Padding codewords
  const pads = [0xec, 0x11];
  for (let p = 0; bits.length < dataCW * 8; p++) for (let i = 7; i >= 0; i--) bits.push((pads[p % 2] >> i) & 1);
  // Pack into bytes
  const codewords = new Uint8Array(dataCW);
  for (let i = 0; i < dataCW; i++) {
    let b = 0;
    for (let j = 0; j < 8; j++) b = (b << 1) | (bits[i * 8 + j] || 0);
    codewords[i] = b;
  }
  return codewords;
}

function placeDataBits(mod, isFn, size, allBits) {
  let bit = 0;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let vert = 0; vert < size; vert++) {
      const row = (Math.floor((size - 1 - right) / 2) % 2 === 0) ? size - 1 - vert : vert;
      for (let lr = 0; lr < 2; lr++) {
        const col = right - lr;
        if (!isFn[row][col]) {
          mod[row][col] = (bit < allBits.length && allBits[bit]) ? 1 : -1;
          bit++;
        }
      }
    }
  }
}

function applyMask(mod, isFn, size, maskId) {
  const fns = [
    (r, c) => (r + c) % 2 === 0,
    (r) => r % 2 === 0,
    (_r, c) => c % 3 === 0,
    (r, c) => (r + c) % 3 === 0,
    (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
    (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
    (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
    (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
  ];
  const fn = fns[maskId];
  for (let r = 0; r < size; r++) for (let c = 0; c < size; c++) {
    if (!isFn[r][c] && fn(r, c)) mod[r][c] *= -1;
  }
}

function penalty(mod, size) {
  let score = 0;
  // Rule 1: 5+ consecutive same-color
  for (let r = 0; r < size; r++) {
    for (let c = 0, run = 1; c < size - 1; c++) {
      if (mod[r][c] === mod[r][c + 1]) { run++; if (run === 5) score += 3; else if (run > 5) score++; }
      else run = 1;
    }
  }
  for (let c = 0; c < size; c++) {
    for (let r = 0, run = 1; r < size - 1; r++) {
      if (mod[r][c] === mod[r + 1][c]) { run++; if (run === 5) score += 3; else if (run > 5) score++; }
      else run = 1;
    }
  }
  return score;
}

function generateQR(text) {
  const bytes = new TextEncoder().encode(text);
  const version = pickVersion(bytes.length);
  const [dataCW, ecCW] = VERSION_TABLE[version];
  const size = 4 * version + 17;

  // Encode + EC
  const data = encodeData(text, version);
  const ec = rsEncode(data, ecCW);
  const allCW = [...data, ...ec];
  const allBits = [];
  for (const cw of allCW) for (let i = 7; i >= 0; i--) allBits.push((cw >> i) & 1);

  // Try masks 0-7, pick lowest penalty
  let bestScore = Infinity, bestMask = 0, bestMod = null;
  for (let maskId = 0; maskId < 8; maskId++) {
    const { mod, isFn } = buildMatrix(version);
    placeDataBits(mod, isFn, size, allBits);
    applyMask(mod, isFn, size, maskId);
    placeFormatInfo(mod, isFn, size, maskId);
    const score = penalty(mod, size);
    if (score < bestScore) { bestScore = score; bestMask = maskId; bestMod = mod; }
  }

  return { mod: bestMod, size };
}

// ── React SVG component ───────────────────────────────────────────────────────
export default function QRCodeSVG({ value = '', size = 120, margin = 2, color = '#000000', bg = 'transparent' }) {
  let qr;
  try { qr = generateQR(value || ' '); }
  catch { return null; }

  const { mod, size: n } = qr;
  const total = n + margin * 2;
  const cell = size / total;

  const rects = [];
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) {
    if (mod[r][c] === 1) {
      rects.push(
        <rect key={`${r}-${c}`}
          x={(c + margin) * cell} y={(r + margin) * cell}
          width={cell} height={cell}
          fill={color}
        />
      );
    }
  }

  return (
    <svg
      width={size} height={size}
      viewBox={`0 0 ${size} ${size}`}
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block' }}
    >
      {bg !== 'transparent' && <rect width={size} height={size} fill={bg}/>}
      {rects}
    </svg>
  );
}
